import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (username, email, password, website = '', recaptchaToken = null) => 
    api.post('/auth/register', { username, email, password, website, recaptchaToken }),
  verify: () => api.get('/auth/verify'),
  getProfile: () => api.get('/auth/profile'),
  updateCredentials: (apiKey, pin, authEndpoint) => 
    api.post('/auth/credentials', { apiKey, pin, authEndpoint }),
  testCredentials: (apiKey, pin, authEndpoint) => 
    api.post('/auth/test-credentials', { apiKey, pin, authEndpoint }),
  testStoredCredentials: () => api.post('/auth/test-stored-credentials'),
  refreshToken: () => api.post('/auth/refresh-token'),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

// Dashboard API
export const dashboardAPI = {
  getLayout: () => api.get('/dashboard/layout'),
  saveLayout: (layout) => api.post('/dashboard/layout', { layout }),
  
  // KPI functions
  getAvailableKpis: () => api.get('/dashboard/available-kpis'),
  getMyKpis: () => api.get('/dashboard/my-kpis'),
  addKpi: (kpiConfigId, positionX, positionY, size, customName, groupName) => 
    api.post('/dashboard/add-kpi', { kpiConfigId, positionX, positionY, size, customName, groupName }),
  updateKpiPosition: (kpiId, positionX, positionY) => 
    api.put(`/dashboard/kpi/${kpiId}/position`, { positionX, positionY }),
  removeKpi: (kpiId) => api.delete(`/dashboard/kpi/${kpiId}`),
  updateKpiName: (kpiId, customName) => api.put(`/dashboard/kpi/${kpiId}/name`, { customName }),
  getKpiData: (kpiId) => api.get(`/dashboard/kpi/${kpiId}/data`),
  getAllKpiData: (skipCache = false) => api.get(`/dashboard/kpi-data${skipCache ? '?skipCache=true' : ''}`),
  
  // KPI Group functions
  getGroups: () => api.get('/dashboard/groups'),
  createGroup: (name, groupOrder = 0) => api.post('/dashboard/groups', { name, groupOrder }),
  updateGroupCollapseState: (groupId, isCollapsed) => 
    api.put(`/dashboard/groups/${groupId}/collapse`, { isCollapsed }),
  updateGroupName: (groupId, name) => api.put(`/dashboard/groups/${groupId}/name`, { name }),
  updateGroupOrder: (groupId, groupOrder) => api.put(`/dashboard/groups/${groupId}/order`, { groupOrder }),
  deleteGroup: (groupId) => api.delete(`/dashboard/groups/${groupId}`),
  updateKpiGroup: (kpiId, groupName, groupOrder = 0) => 
    api.put(`/dashboard/kpi/${kpiId}/group`, { groupName, groupOrder }),
  
  // Data Grid functions
  getAvailableDataGrids: () => api.get('/dashboard/available-data-grids'),
  getMyDataGrids: () => api.get('/dashboard/my-data-grids'),
  addDataGrid: (dataGridConfigId, positionX, positionY, size, customName) => 
    api.post('/dashboard/add-data-grid', { dataGridConfigId, positionX, positionY, size, customName }),
  updateDataGridPosition: (dataGridId, positionX, positionY) => 
    api.put(`/dashboard/data-grid/${dataGridId}/position`, { positionX, positionY }),
  updateDataGridName: (dataGridId, customName) => 
    api.put(`/dashboard/data-grid/${dataGridId}/name`, { customName }),
  updateDataGridSize: (dataGridId, size) => 
    api.put(`/dashboard/data-grid/${dataGridId}/size`, { size }),
  removeDataGrid: (dataGridId) => api.delete(`/dashboard/data-grid/${dataGridId}`),
  getDataGridData: (dataGridId) => api.get(`/dashboard/data-grid/${dataGridId}/data`),
  getAllDataGridData: (skipCache = false) => api.get(`/dashboard/data-grid-data${skipCache ? '?skipCache=true' : ''}`),
  saveDataGridOrder: (dataGrids) => api.post('/dashboard/save-data-grid-order', { dataGrids }),
  
  // Action functions
  getAvailableActions: () => api.get('/dashboard/available-actions'),
  getMyActions: () => api.get('/dashboard/my-actions'),
  addAction: (actionConfigId, customName, displayOrder) => 
    api.post('/dashboard/add-action', { actionConfigId, customName, displayOrder }),
  updateActionName: (actionId, customName) => 
    api.put(`/dashboard/action/${actionId}/name`, { customName }),
  updateActionOrder: (actionId, displayOrder) => 
    api.put(`/dashboard/action/${actionId}/order`, { displayOrder }),
  removeAction: (actionId) => api.delete(`/dashboard/action/${actionId}`),
  executeAction: (actionConfigId, formData) => 
    api.post('/dashboard/execute-action', { actionConfigId, formData }),
  getActionExecutions: (actionConfigId, limit, offset) => {
    const params = new URLSearchParams();
    if (actionConfigId) params.append('actionConfigId', actionConfigId);
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    return api.get(`/dashboard/action-executions?${params.toString()}`);
  },
};

// Admin API
export const adminAPI = {
  // Endpoints
  getEndpoints: () => api.get('/admin/endpoints'),
  createEndpoint: (endpoint) => api.post('/admin/endpoints', endpoint),
  updateEndpoint: (endpointId, endpoint) => api.put(`/admin/endpoints/${endpointId}`, endpoint),
  deleteEndpoint: (endpointId) => api.delete(`/admin/endpoints/${endpointId}`),
  testEndpoint: (endpoint) => api.post('/admin/test-endpoint', endpoint),
  
  // KPI Configs
  getKpiConfigs: () => api.get('/admin/kpi-configs'),
  createKpiConfig: (kpiConfig) => api.post('/admin/kpi-configs', kpiConfig),
  updateKpiConfig: (kpiConfigId, kpiConfig) => api.put(`/admin/kpi-configs/${kpiConfigId}`, kpiConfig),
  deleteKpiConfig: (kpiConfigId) => api.delete(`/admin/kpi-configs/${kpiConfigId}`),
  testKpiConfig: (config) => api.post('/admin/test-kpi-config', config),
  
  // Data Grid Configs
  getDataGridConfigs: () => api.get('/admin/data-grid-configs'),
  createDataGridConfig: (dataGridConfig) => api.post('/admin/data-grid-configs', dataGridConfig),
  updateDataGridConfig: (dataGridConfigId, dataGridConfig) => api.put(`/admin/data-grid-configs/${dataGridConfigId}`, dataGridConfig),
  deleteDataGridConfig: (dataGridConfigId) => api.delete(`/admin/data-grid-configs/${dataGridConfigId}`),
  
  // Action Configs
  getActionConfigs: () => api.get('/admin/action-configs'),
  createActionConfig: (actionConfig) => api.post('/admin/action-configs', actionConfig),
  updateActionConfig: (actionConfigId, actionConfig) => api.put(`/admin/action-configs/${actionConfigId}`, actionConfig),
  deleteActionConfig: (actionConfigId) => api.delete(`/admin/action-configs/${actionConfigId}`),
  getActionExecutions: (actionConfigId, userId, limit, offset) => {
    const params = new URLSearchParams();
    if (actionConfigId) params.append('actionConfigId', actionConfigId);
    if (userId) params.append('userId', userId);
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    return api.get(`/admin/action-executions?${params.toString()}`);
  },
  
  // Users
  getUsers: () => api.get('/admin/users'),
  createUser: (userData) => api.post('/admin/users', userData),
  updateUser: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // Security
  getSecurityEvents: (limit, offset, eventType, severity) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    if (eventType) params.append('eventType', eventType);
    if (severity) params.append('severity', severity);
    return api.get(`/admin/security-events?${params.toString()}`);
  },
  getSecurityStats: () => api.get('/admin/security-stats'),
};

export default api;
